/**
 * Créer ou modifier une tâche.
 *
 * Deux niveaux, comme prévu à la conception : le titre et la catégorie suffisent
 * pour enregistrer, le reste se déplie. Taper « changer le joint du robinet,
 * tous les 6 mois » doit prendre dix secondes et ne pas obliger à traverser un
 * formulaire de douze champs.
 */

import { useState } from 'react';

import {
  type Categorie,
  type PeriodeVacances,
  type Tache,
  type Utilisateur,
  CATEGORIES,
  LIBELLE_CATEGORIE,
  aujourdhuiParis,
} from '@foyer/noyau';

import {
  type EtatRecurrence,
  RECURRENCE_INITIALE,
  SelecteurRecurrence,
  depuisRecurrence,
  versRecurrence,
} from '../composants/SelecteurRecurrence.js';
import { Champ, Feuille } from '../composants/ui.js';
import { donnees } from '../lib/donnees.js';
import { useAppli } from '../lib/appli.js';

export interface ProprietesFeuilleTache {
  tache?: Tache;
  /** Titre pré-rempli depuis la barre d'ajout rapide. */
  titreInitial?: string;
  categorieInitiale?: Categorie;
  /** Ouvre la feuille sur « sans date », pour l'écran qui ne contient que ça. */
  sansDateInitiale?: boolean;
  equipementId?: string;
  personneId?: string;
  membres?: Utilisateur[];
  periodes?: PeriodeVacances[];
  onFerme(): void;
  onEnregistre?(tache: Tache): void;
}

export function FeuilleTache({
  tache,
  titreInitial = '',
  categorieInitiale = 'maison',
  sansDateInitiale = false,
  equipementId,
  personneId,
  membres = [],
  periodes = [],
  onFerme,
  onEnregistre,
}: ProprietesFeuilleTache) {
  const { rafraichis } = useAppli();
  const aujourdhui = aujourdhuiParis();

  const [titre, setTitre] = useState(tache?.titre ?? titreInitial);
  const [categorie, setCategorie] = useState<Categorie>(tache?.categorie ?? categorieInitiale);
  const [echeance, setEcheance] = useState(tache?.echeance ?? aujourdhui);
  const [sansDate, setSansDate] = useState(tache ? tache.echeance === null : sansDateInitiale);
  const [assigneA, setAssigneA] = useState(tache?.assigneA ?? '');
  const [notes, setNotes] = useState(tache?.notes ?? '');
  const [rappels, setRappels] = useState((tache?.rappels ?? [7, 1, 0]).join(', '));
  const [priorite, setPriorite] = useState(tache?.priorite ?? '');
  const [coutEstime, setCoutEstime] = useState(tache?.coutEstime?.toString() ?? '');
  const [escaladeJours, setEscaladeJours] = useState(tache?.escaladeJours?.toString() ?? '');
  const [escaladeVers, setEscaladeVers] = useState(tache?.escaladeVers ?? '');
  const [deplie, setDeplie] = useState(Boolean(tache));
  const [recurrence, setRecurrence] = useState<EtatRecurrence>(
    tache
      ? depuisRecurrence(tache.rrule, tache.ancrageVacances, tache.recurrenceBase)
      : RECURRENCE_INITIALE,
  );

  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  async function enregistre() {
    setErreur(null);
    setOccupe(true);
    try {
      const r = versRecurrence(recurrence, sansDate ? aujourdhui : echeance);
      const corps: Record<string, unknown> = {
        titre,
        categorie,
        echeance: sansDate ? null : echeance,
        rrule: r.rrule,
        recurrenceDepart: r.depart,
        recurrenceBase: r.base,
        ancrageVacances: r.ancrage,
        rappels: rappels
          .split(/[,\s]+/)
          .map((n) => Number(n))
          .filter((n) => Number.isInteger(n) && n >= 0),
        notes: notes || null,
        assigneA: assigneA || null,
        priorite: priorite || null,
        coutEstime: coutEstime === '' ? null : Number(coutEstime),
        escaladeJours: escaladeJours === '' ? null : Number(escaladeJours),
        escaladeVers: escaladeVers || null,
      };
      if (!tache) {
        if (equipementId) corps.equipementId = equipementId;
        if (personneId) corps.personneId = personneId;
      }

      const reponse = tache
        ? await donnees.majTache(tache.id, corps)
        : await donnees.creeTache(corps);
      rafraichis();
      onEnregistre?.(reponse.tache);
      onFerme();
    } catch (err) {
      setErreur((err as Error).message);
    } finally {
      setOccupe(false);
    }
  }

  async function supprime() {
    if (!tache) return;
    setOccupe(true);
    try {
      await donnees.supprimeTache(tache.id);
      rafraichis();
      onFerme();
    } catch (err) {
      setErreur((err as Error).message);
      setOccupe(false);
    }
  }

  return (
    <Feuille
      titre={tache ? 'Modifier la tâche' : 'Nouvelle tâche'}
      onFerme={onFerme}
      pied={
        <>
          <button type="button" className="bouton" onClick={enregistre} disabled={occupe || !titre.trim()}>
            {occupe ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          {tache && (
            <button type="button" className="bouton secondaire" onClick={supprime} disabled={occupe}>
              Supprimer
            </button>
          )}
        </>
      }
    >
      {tache?.modeleCle && !tache.interventionUtilisateur && (
        <div className="note">
          Cette tâche vient du catalogue. La modifier la met à votre main : les mises à jour
          automatiques ne l’écraseront plus.
        </div>
      )}

      <Champ etiquette="Intitulé">
        <input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Changer le joint du robinet" autoFocus />
      </Champ>

      <div className="rangee">
        <Champ etiquette="Catégorie">
          <select value={categorie} onChange={(e) => setCategorie(e.target.value as Categorie)}>
            {CATEGORIES.filter((c) => c !== 'courses').map((c) => (
              <option key={c} value={c}>
                {LIBELLE_CATEGORIE[c]}
              </option>
            ))}
          </select>
        </Champ>
        <Champ etiquette="Échéance">
          <input
            type="date"
            value={echeance}
            disabled={sansDate}
            onChange={(e) => setEcheance(e.target.value)}
          />
        </Champ>
      </div>

      <label className="ligne-reglage" style={{ borderBottom: 0, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={sansDate}
          onChange={(e) => setSansDate(e.target.checked)}
          style={{ width: 22, height: 22, minHeight: 0, flex: '0 0 auto' }}
        />
        <span className="texte">
          Sans date
          <div className="sourdine">Pour un chantier à faire un jour, sans échéance.</div>
        </span>
      </label>

      <SelecteurRecurrence
        etat={recurrence}
        onChange={setRecurrence}
        depart={sansDate ? aujourdhui : echeance}
        periodes={periodes}
      />

      {!deplie ? (
        <button type="button" className="bouton texte" onClick={() => setDeplie(true)}>
          Plus d’options…
        </button>
      ) : (
        <>
          <div className="rangee">
            <Champ etiquette="Assignée à">
              <select value={assigneA} onChange={(e) => setAssigneA(e.target.value)}>
                <option value="">tout le monde</option>
                {membres.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.prenom}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ etiquette="Rappels (jours avant)" aide="Séparés par des virgules.">
              <input value={rappels} onChange={(e) => setRappels(e.target.value)} placeholder="30, 7, 1, 0" />
            </Champ>
          </div>

          {/*
            La priorité vaut pour toute tâche, pas seulement pour un chantier :
            c'est elle qui range l'écran « Quand j'ai le temps », et une tâche
            sans date n'a rien d'autre pour se faire classer. Le coût estimé,
            lui, reste un sujet de travaux.
          */}
          <div className="rangee">
            <Champ etiquette="Priorité" aide="Sert à ranger les tâches sans date.">
              <select value={priorite} onChange={(e) => setPriorite(e.target.value as typeof priorite)}>
                <option value="">non précisée</option>
                <option value="haute">haute</option>
                <option value="moyenne">moyenne</option>
                <option value="basse">basse</option>
              </select>
            </Champ>
            {categorie === 'travaux' && (
              <Champ etiquette="Coût estimé (€)">
                <input
                  type="number"
                  min={0}
                  value={coutEstime}
                  onChange={(e) => setCoutEstime(e.target.value)}
                />
              </Champ>
            )}
          </div>

          {membres.length > 1 && (
            <div className="rangee">
              <Champ
                etiquette="Escalade après (jours)"
                aide="Passé ce retard, l’autre parent est prévenu."
              >
                <input
                  type="number"
                  min={1}
                  value={escaladeJours}
                  onChange={(e) => setEscaladeJours(e.target.value)}
                />
              </Champ>
              <Champ etiquette="Prévenir">
                <select value={escaladeVers} onChange={(e) => setEscaladeVers(e.target.value)}>
                  <option value="">personne</option>
                  {membres.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.prenom}
                    </option>
                  ))}
                </select>
              </Champ>
            </div>
          )}

          <Champ etiquette="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Champ>
        </>
      )}

      {erreur && <div className="alerte">{erreur}</div>}
    </Feuille>
  );
}
